import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from './cx.js';
import { Icon, type IconName } from './Icon.tsx';
import { Button } from './Button.tsx';

/**
 * The states every screen owes a person.
 *
 * An empty state teaches the next action. It does not advertise, and it does
 * not describe the situation the person can already see - "No contacts yet" is
 * a caption, not help.
 */

export type EmptyReason =
  /** Genuinely nothing here yet. */
  | 'no-records'
  /** There are records; the filters are hiding them. A different problem. */
  | 'filtered-to-nothing'
  /** There may be records; this person is not allowed to know. */
  | 'permission-denied'
  /**
   * Waiting for the person to say what they want.
   *
   * A search page opened with no query has nothing to show and nothing is
   * wrong: the next action is to type, and the state says so. Distinct from
   * 'no-records', which claims a search was made and found nothing - a
   * different sentence and a different thing to do about it.
   */
  | 'awaiting-input'
  /**
   * Nothing here, and that is the good outcome.
   *
   * An empty sync queue, a clear alert list, a person with no overdue work.
   * These are the one kind of empty state that owes no next action: offering
   * one would be a control wired to nothing, and telling somebody to go and
   * fix an empty queue of failures teaches them the screen does not know what
   * it is looking at.
   */
  | 'all-clear';

export interface EmptyStateAction {
  label: string;
  onClick?: () => void;
  href?: string;
  icon?: IconName;
}

export function EmptyState({
  reason = 'no-records',
  title,
  body,
  primary,
  secondary,
  tertiary,
  illustration = 'Inbox',
  className,
  ...rest
}: {
  reason?: EmptyReason;
  title: string;
  /** One sentence. */
  body: string;
  primary?: EmptyStateAction;
  secondary?: EmptyStateAction;
  /** Load sample data, usually. */
  tertiary?: EmptyStateAction;
  illustration?: string;
  className?: string;
} & HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div
      {...rest}
      data-component="EmptyState"
      data-empty-reason={reason}
      className={cx('flex flex-col items-center gap-3 px-6 py-7 text-center', className)}
    >
      <span className="flex h-touch w-touch items-center justify-center rounded-chip bg-sunken text-ink-400">
        <Icon name={illustration} decorative />
      </span>
      <h2 className="text-lead font-medium text-ink">{title}</h2>
      <p className="max-w-reading text-body text-ink-meta">{body}</p>
      {primary || secondary || tertiary ? (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
          {primary ? <ActionButton action={primary} intent="primary" /> : null}
          {secondary ? <ActionButton action={secondary} intent="secondary" /> : null}
          {tertiary ? <ActionButton action={tertiary} intent="ghost" /> : null}
        </div>
      ) : null}
    </div>
  );
}

function ActionButton({
  action,
  intent,
}: {
  action: EmptyStateAction;
  intent: 'primary' | 'secondary' | 'ghost';
}): JSX.Element {
  if (action.href) {
    return (
      <a
        href={action.href}
        data-component="Button"
        className={cx(
          'inline-flex h-btn items-center justify-center gap-2 rounded-control px-btn-x text-body font-medium',
          intent === 'primary'
            ? 'bg-bronze text-bronze-on hover:bg-bronze-hover'
            : intent === 'secondary'
              ? 'border border-control bg-surface text-ink hover:bg-sunken'
              : 'text-ink-600 hover:bg-sunken hover:text-ink',
        )}
      >
        {action.icon ? <Icon name={action.icon} size="inline" decorative /> : null}
        {action.label}
      </a>
    );
  }
  return (
    <Button intent={intent} onClick={action.onClick} {...(action.icon ? { icon: action.icon } : {})}>
      {action.label}
    </Button>
  );
}

/**
 * Why something is not available.
 *
 * Four kinds, four next steps, never collapsed into one. They are genuinely
 * different situations and the person can act on three of them:
 *
 *   plan-locked     - a higher plan includes it. Show what, and where to look.
 *   module-off      - the plan includes it; an administrator switched it off.
 *   not-configured  - a provider needs credentials before this can run.
 *   not-built       - specified, not implemented in this deployment yet.
 *
 * The last one is the reason this component exists at all. A feature that is
 * not built renders this, naming itself, rather than a broken screen or a
 * convincing-looking pretence.
 */
export type UnavailableKind = 'plan-locked' | 'module-off' | 'not-configured' | 'not-built';

const KIND_ICON: Record<UnavailableKind, string> = {
  'plan-locked': 'Lock',
  'module-off': 'ToggleLeft',
  'not-configured': 'Plug',
  'not-built': 'Hammer',
};

export function CapabilityUnavailable({
  kind,
  capability,
  title,
  body,
  action,
  className,
  ...rest
}: {
  kind: UnavailableKind;
  /** What is unavailable, named. */
  capability: string;
  title: string;
  body: string;
  action?: EmptyStateAction;
  className?: string;
} & HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div
      {...rest}
      data-component="CapabilityUnavailable"
      data-unavailable-kind={kind}
      data-capability={capability}
      className={cx('flex flex-col items-start gap-3 rounded-card bg-sunken p-5', className)}
    >
      <span className="flex items-center gap-2 text-ink-600">
        <Icon name={KIND_ICON[kind]} size="inline" decorative />
        <span className="text-micro uppercase tracking-label text-ink-label">{capability}</span>
      </span>
      <h2 className="text-lead font-medium text-ink">{title}</h2>
      <p className="max-w-reading text-body text-ink-meta">{body}</p>
      {action ? <ActionButton action={action} intent="secondary" /> : null}
    </div>
  );
}

/**
 * An inline failure.
 *
 * Names what failed, names the rule if a rule fired, and offers the retry.
 * Page-level errors are for the case where the page genuinely cannot render;
 * everything else is a region that failed while the rest of the screen works.
 */
export function ErrorState({
  title,
  body,
  rule,
  onRetry,
  retryLabel = 'Try again',
  className,
  ...rest
}: {
  title: string;
  body: string;
  /** The named rule that produced this, when one did. */
  rule?: string;
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
} & HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div
      {...rest}
      data-component="ErrorState"
      role="alert"
      className={cx('flex flex-col items-start gap-2 rounded-card bg-sunken p-4', className)}
    >
      <span className="flex items-center gap-2 text-danger">
        <Icon name="Warning" size="inline" decorative />
        <span className="text-body font-medium">{title}</span>
      </span>
      <p className="text-dense text-ink-meta">{body}</p>
      {rule ? (
        <p className="text-meta text-ink-meta">
          Rule: <span className="font-medium text-ink">{rule}</span>
        </p>
      ) : null}
      {onRetry ? (
        <Button intent="secondary" size="small" icon="ArrowCounterClockwise" onClick={onRetry}>
          {retryLabel}
        </Button>
      ) : null}
    </div>
  );
}

export function VisuallyHidden({
  children,
  ...rest
}: { children: ReactNode } & HTMLAttributes<HTMLSpanElement>): JSX.Element {
  return (
    <span {...rest} className="sr-only">
      {children}
    </span>
  );
}
